import { BandChart } from '@design/charts/band-chart/BandChart';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useThemePerformanceTimeseries } from '@terminal/react-query-hooks/themes/useThemePerformanceTimeseries';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { HorizonSelector } from '@terminal/widgets/theme-widget/tabs/theme-overview-tab/components/horizon-selector/HorizonSelector';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChartTitle,
  isValidHorizon,
  PickedThemePerformanceSortHorizon,
} from './chart-title/ChartTitle';
import styles from './ThemePerformanceSection.module.scss';
import { ThemePerformanceSectionContent } from './ThemePerformanceSectionContent';

export type Horizon = '1D' | '1W' | '1M' | '3M' | '6M' | '1Y';

type ThemePerformanceSectionProps = {
  themeId: string;
  entityTags: string[];
  customHeaderDescription?: ReactNode;
  isFetching?: boolean;
  isError?: boolean;
  error?: Error | null;
};

export const ThemePerformanceSection = ({
  themeId,
  entityTags,
  customHeaderDescription,
  isFetching,
  isError,
  error = null,
}: ThemePerformanceSectionProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft']);
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const isIntradayChartEnabled = useFlags(state =>
    state.getFeatureFlagState('theme_intraday_chart')
  );

  const { params, setQueryParams } = useQueryParams();
  const defaultHorizon: PickedThemePerformanceSortHorizon =
    isIntradayChartEnabled ? 'intraday' : '1y';
  const themeHorizonParam = params[queryKeys.themeHorizon];
  const isHorizonParamUsable =
    isValidHorizon(themeHorizonParam) &&
    (themeHorizonParam === 'intraday' ? isIntradayChartEnabled : true);
  // The horizon is only driven by search params when the drawer was opened
  // with one already present (e.g. deep-linked). Otherwise it's local state,
  // so the drawer works the same when opened without search params (e.g.
  // from ManageFollowingThemesTab).
  const isHorizonControlledByQueryParams = useRef(isHorizonParamUsable).current;
  const [horizon, setHorizon] = useState<PickedThemePerformanceSortHorizon>(
    () => (isHorizonParamUsable ? themeHorizonParam : defaultHorizon)
  );

  const handleHorizonChange = (
    newHorizon: PickedThemePerformanceSortHorizon
  ) => {
    setHorizon(newHorizon);
    if (isHorizonControlledByQueryParams) {
      setQueryParams({ [queryKeys.themeHorizon]: newHorizon });
    }
  };

  const {
    data: themePerformance,
    isFetching: isThemePerformanceFetching,
    isError: isThemePerformanceError,
    error: themePerformanceError,
  } = useThemePerformanceTimeseries({ themeId, horizon }, !!themeId);

  const aggregate = themePerformance?.aggregate;

  const total = entityTags.length > 10 ? 10 : entityTags.length;

  const formatData = (data?: { index: string; value: number }[]) =>
    data?.map((d, idx) => ({
      index: idx,
      value: d.value,
      date: new Date(d.index),
    })) ?? [];

  const bandChartWrapperStyle: Record<string, string> = useMemo(
    () => ({
      '--band-chart-line-color': colors.default.value,
    }),
    [colors.default.value]
  );

  return (
    <ThemePerformanceSectionContent
      isLoading={isThemePerformanceFetching || !!isFetching}
      isError={isThemePerformanceError || !!isError}
      isEmpty={!aggregate?.length}
      error={!!isError ? error : themePerformanceError}
    >
      <div className={styles.chartAndHorizonsWrapper}>
        <HorizonSelector
          activeHorizon={horizon}
          onChange={handleHorizonChange}
        />
        <ChartTitle
          activeHorizon={horizon}
          horizonReturnValue={aggregate?.at(-1)?.value}
          description={
            customHeaderDescription ??
            `${t('themeOverview.equalWeightedBasket')} ${t(
              'themeOverview.topCompanies',
              { total }
            )}`
          }
        />
        <div className={styles.bandChartWrapper} style={bandChartWrapperStyle}>
          <BandChart
            tsData={formatData(aggregate)}
            showLine
            domainLimited={false}
            includeXScalePadding={false}
            isBordered
            fillColors={{
              positive: colors.threshold.positive.value,
              negative: colors.threshold.negative.value,
              singlePointPositive: colors.bars.up.value,
              singlePointNegative: colors.bars.down.value,
            }}
            isPercent
            locale={languageSetting}
          />
        </div>
      </div>
    </ThemePerformanceSectionContent>
  );
};
